import {
    Avatar,
    Box,
    Button,
    Card,
    Checkbox,
    Grid,
    Input,
    Stack,
    Typography,
    Alert,
} from "@mui/joy";
import { AlertCircle, Building2, CheckCircle2, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { SideModal } from "../../../";
import { SupplierComparison as PriceComparison } from "../../../Supplier/PriceComparison";
import {
    materialGlassPanelSx,
    MATERIAL_ROW,
} from "../LineItems/materialRowLayout";
import { useLaborSupplierComparison } from "../../hooks/useLaborSupplierComparison";
import type { LaborRowItem } from "../../types";

type LaborSupplierComparisonProps = {
    projectId?: string;
    laborRows: LaborRowItem[];
};

const inlineAlertSx = {
    mb: 2,
    py: 1,
    px: 1.5,
    borderRadius: "4px",
    alignItems: "center",
    gap: 1,
    border: "1px solid",
    borderColor: "warning.outlinedBorder",
    bgcolor: "warning.softBg",
    "& .MuiAlert-message": {
        py: 0,
    },
} as const;

function SupplierInlineAlert({ message }: { message: string }) {
    return (
        <Alert
            variant="soft"
            color="warning"
            size="md"
            startDecorator={<AlertCircle size={16} />}
            sx={inlineAlertSx}
        >
            {message}
        </Alert>
    );
}

function SupplierComparisonSkeleton() {
    return (
        <Box
            sx={{
                ...materialGlassPanelSx,
                mx: { xs: -2, sm: -3 },
            }}
        >
            <Box
                sx={{
                    px: MATERIAL_ROW.px,
                    py: { xs: 2, sm: 2.5 },
                }}
            >
                <Typography level="title-md" sx={{ mb: 2 }}>
                    Labor Supplier Comparison
                </Typography>
                <Stack direction="row" spacing={1.5}>
                    {[1, 2, 3].map((item) => (
                        <Box
                            key={item}
                            sx={{
                                width: 160,
                                height: 52,
                                borderRadius: "4px",
                                bgcolor: "background.level1",
                            }}
                        />
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}

export default function LaborSupplierComparison({
    projectId,
    laborRows,
}: LaborSupplierComparisonProps) {
    const {
        suppliers,
        comparableItemCount,
        canCompare,
        hasUnsavedLaborChanges,
        selectedSupplierNames,
        setSelectedSupplierNames,
        openModal,
        setOpenModal,
        comparisonResults,
        isComparing,
        isConfirming,
        isLoadingSuppliers,
        localSelections,
        itemSupplierSelections,
        handleCompare,
        onItemSupplierSelect,
        onSupplierColumnSelectAll,
        handleConfirm,
    } = useLaborSupplierComparison({
        projectId,
        laborRows,
    });

    const [searchTerm, setSearchTerm] = useState("");

    const filteredSuppliers = useMemo(() => {
        if (!searchTerm) return suppliers;
        return suppliers.filter((supplier: any) =>
            supplier.Name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [suppliers, searchTerm]);

    const displaySuppliers = filteredSuppliers.slice(0, 5);
    const remainingCount = Math.max(filteredSuppliers.length - 5, 0);

    const assignedSupplierCounts = useMemo(() => {
        const counts: Record<string, number> = {};
        for (const supplierName of Object.values(itemSupplierSelections)) {
            counts[supplierName] = (counts[supplierName] || 0) + 1;
        }
        return counts;
    }, [itemSupplierSelections]);

    if (isLoadingSuppliers) {
        return <SupplierComparisonSkeleton />;
    }

    return (
        <Box
            sx={{
                ...materialGlassPanelSx,
                mx: { xs: -2, sm: -3 },
            }}
        >
            <Box
                sx={{
                    px: MATERIAL_ROW.px,
                    py: { xs: 2, sm: 2.5 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                        gap: 2,
                        mb: 2,
                    }}
                >
                    <Box>
                        <Typography level="title-md">
                            Labor Supplier Comparison
                        </Typography>
                        <Typography level="body-sm" color="neutral">
                            Compare crew pricing for saved labor line items.
                        </Typography>
                    </Box>
                    <Input
                        size="sm"
                        placeholder="Search suppliers..."
                        startDecorator={<Search size={16} />}
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        sx={{
                            minWidth: { xs: "100%", sm: 240 },
                            borderRadius: "4px",
                        }}
                    />
                </Box>

                {hasUnsavedLaborChanges && (
                    <SupplierInlineAlert message="Save labor changes before comparing suppliers." />
                )}

                {!hasUnsavedLaborChanges && comparableItemCount === 0 && (
                    <SupplierInlineAlert message="Set labor quantities and save to enable supplier comparison." />
                )}

                {selectedSupplierNames.length === 0 && canCompare && (
                    <SupplierInlineAlert message="Select at least one supplier to compare pricing." />
                )}
                <Grid
                    container
                    spacing={1.5}
                    sx={{
                        m: 0,
                        ml: -1,
                        width: "100%",
                    }}
                >
                    {displaySuppliers.map((supplier: any) => {
                        const isSelected = selectedSupplierNames.includes(
                            supplier.Name,
                        );
                        const assignedItemCount =
                            assignedSupplierCounts[supplier.Name] || 0;
                        const isAssigned = assignedItemCount > 0;

                        return (
                            <Grid key={supplier.Id} xs={12} sm={4} md={3}>
                                <Card
                                    component="label"
                                    variant={
                                        isSelected || isAssigned
                                            ? "soft"
                                            : "outlined"
                                    }
                                    color={
                                        isSelected
                                            ? "primary"
                                            : isAssigned
                                              ? "success"
                                              : "neutral"
                                    }
                                    sx={{
                                        cursor: "pointer",
                                        position: "relative",
                                        overflow: "visible",
                                        borderRadius: "4px",
                                        border: isSelected
                                            ? "2px solid"
                                            : "1px solid",
                                        borderColor: isSelected
                                            ? "primary.solidBg"
                                            : isAssigned
                                              ? "success.outlinedBorder"
                                              : "divider",
                                        minHeight: "auto",
                                        py: 0.5,
                                        "&:hover": {
                                            borderColor: isSelected
                                                ? "primary.solidBg"
                                                : isAssigned
                                                  ? "success.outlinedBorder"
                                                  : "neutral.outlinedBorder",
                                            bgcolor: isSelected
                                                ? "primary.softHoverBg"
                                                : isAssigned
                                                  ? "success.softHoverBg"
                                                  : "background.level1",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: -5,
                                            right: -5,
                                            display:
                                                isSelected || isAssigned
                                                    ? "flex"
                                                    : "none",
                                            bgcolor: isSelected
                                                ? "primary.solidBg"
                                                : "success.solidBg",
                                            color: isSelected
                                                ? "primary.solidColor"
                                                : "success.solidColor",
                                            borderRadius: "50%",
                                            p: 0.25,
                                            zIndex: 1,
                                            border: "1px solid",
                                            borderColor: "background.surface",
                                        }}
                                    >
                                        <CheckCircle2
                                            size={12}
                                            strokeWidth={3}
                                        />
                                    </Box>

                                    <Checkbox
                                        checked={isSelected}
                                        onChange={(event) => {
                                            if (event.target.checked) {
                                                setSelectedSupplierNames(
                                                    (prev) => [
                                                        ...prev,
                                                        supplier.Name,
                                                    ],
                                                );
                                            } else {
                                                setSelectedSupplierNames(
                                                    (prev) =>
                                                        prev.filter(
                                                            (name) =>
                                                                name !==
                                                                supplier.Name,
                                                        ),
                                                );
                                            }
                                        }}
                                        sx={{ display: "none" }}
                                    />

                                    <Box sx={{ p: 0.5, px: 1 }}>
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >
                                            <Avatar
                                                variant={
                                                    isSelected
                                                        ? "solid"
                                                        : isAssigned
                                                          ? "soft"
                                                          : "soft"
                                                }
                                                color={
                                                    isSelected
                                                        ? "primary"
                                                        : isAssigned
                                                          ? "success"
                                                          : "neutral"
                                                }
                                                size="sm"
                                                sx={{ "--Avatar-size": "24px" }}
                                            >
                                                <Building2 size={14} />
                                            </Avatar>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    level="title-sm"
                                                    noWrap
                                                >
                                                    {supplier.Name}
                                                </Typography>
                                                <Typography
                                                    level="body-xs"
                                                    color={
                                                        isAssigned
                                                            ? "success"
                                                            : "neutral"
                                                    }
                                                    sx={{
                                                        fontWeight: isAssigned
                                                            ? 600
                                                            : 400,
                                                    }}
                                                >
                                                    {isAssigned
                                                        ? `${assignedItemCount} item${assignedItemCount !== 1 ? "s" : ""} assigned`
                                                        : "Labor"}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}

                    {remainingCount > 0 && (
                        <Grid xs={12} sm={4} md={3}>
                            <Card
                                variant="soft"
                                color="neutral"
                                sx={{
                                    height: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: "4px",
                                    border: "1px dashed",
                                    borderColor: "divider",
                                    bgcolor: "background.level1",
                                    py: 0.5,
                                    minHeight: "auto",
                                }}
                            >
                                <Typography
                                    level="body-xs"
                                    fontWeight="bold"
                                    color="neutral"
                                >
                                    + {remainingCount} more
                                </Typography>
                            </Card>
                        </Grid>
                    )}
                </Grid>

                <Button
                    variant="soft"
                    size="sm"
                    color="primary"
                    onClick={handleCompare}
                    loading={isComparing}
                    disabled={!canCompare || selectedSupplierNames.length === 0}
                    sx={{
                        mt: 2,
                        borderRadius: "4px",
                        border: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    Compare Suppliers
                    {comparableItemCount > 0
                        ? ` (${comparableItemCount} items)`
                        : ""}
                </Button>

                <SideModal
                    hideHeader={true}
                    portalBoxProps={{
                        minWidth: "70%",
                        width: 1200,
                        maxWidth: "100vw",
                    }}
                    open={openModal}
                    onClose={() => setOpenModal(false)}
                >
                    <PriceComparison
                        comparisonResults={comparisonResults}
                        isLoading={isComparing || isConfirming}
                        onClose={() => setOpenModal(false)}
                        itemSupplierSelections={localSelections}
                        onItemSupplierSelect={onItemSupplierSelect}
                        onSupplierColumnSelectAll={onSupplierColumnSelectAll}
                        onConfirm={handleConfirm}
                        purchaseStrategy="multiple"
                    />
                </SideModal>
            </Box>
        </Box>
    );
}
